"use client";
import React, { useEffect } from "react";
import Image from "next/image";
import { Button } from "../ui/button";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { useInView } from "react-intersection-observer";
import { ElementType } from "@/constants/tagular/main";

interface GuidedPathProps {
  className?: string;
  onGetStarted?: () => void;
  headingRef?: React.Ref<HTMLHeadingElement>;
}

const EntryPoint: React.FC<GuidedPathProps> = ({
  onGetStarted,
  headingRef,
}) => {
  let ctaText = "Get Started";
  // Banner element tracking
  const bannerWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Banner,
    text: "Find your learning path",
    name: "find-your-path-banner",
  };

  const { elementViewed: bannerElementViewed } = useElementEvents({
    webElement: bannerWebElement,
  });

  const { ref: bannerRef, inView: bannerInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (bannerInView) {
      bannerElementViewed();
    }
  }, [bannerInView, bannerElementViewed]);

  // get started button element tracking
  const buttonWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    text: ctaText,
    name: "find-your-path-banner",
  };

  const {
    elementViewed: buttonElementViewed,
    elementClicked: buttonElementClicked,
  } = useElementEvents({ webElement: buttonWebElement });

  const { ref: buttonRef, inView: buttonInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (buttonInView) {
      buttonElementViewed();
    }
  }, [buttonInView, buttonElementViewed]);

  const handleButtonClick = () => {
    buttonElementClicked();
    onGetStarted?.();
  };

  return (
    <div
      ref={bannerRef}
      className="container font-sans rounded-xl flex flex-col gap-4 lg:flex-row justify-between bg-secondary-700 text-white p-4 lg:p-0"
    >
      <div className="flex flex-col gap-4 lg:w-[667px] lg:pl-14 lg:my-auto">
        <div className="flex items-start lg:items-center gap-2">
          <Image
            src="/icons/ai-icon.png"
            alt="ai-icon"
            width={36}
            height={36}
            className="m-0"
          />

          <h3
            ref={headingRef}
            className="text-2xl lg:text-[41px] italic font-black text-white m-0"
            tabIndex={-1}
          >
            Find your learning path
          </h3>
        </div>
        <span className="text-base lg:text-lg">
          Answer a few questions to get personalized recommendations on what
          learning path is best for you.
        </span>
        <Button
          ref={buttonRef}
          variant={"brand"}
          radius="md"
          className="w-full lg:w-min no-underline lg:py-2 lg:px-6 focus-visible:!ring-2 focus-visible:!ring-white focus-visible:!ring-offset-2 focus-visible:!ring-offset-secondary-700"
          onClick={handleButtonClick}
        >
          {ctaText}
        </Button>
      </div>
      <div className="lg:ml-auto">
        <div className="w-full h-auto items-center mx-auto">
          <Image
            src="https://images.cdn.edx.org/attachment-225044b5951fd9dcf1a878bdbd8d96bba01b5d8eee25f.jpg"
            alt=""
            width={419}
            height={331}
            className="m-0 lg:clip-slant-left rounded-xl mx-auto"
          />
        </div>
      </div>
    </div>
  );
};

export default EntryPoint;
